<template>
  <Card
    :card-class-names="cardClassNames"
    :has-body-padding-small="true"
    header-class-names="tw-flex tw-justify-flex-start tw-flex-wrap tw-px-2 tw-py-4"
  >
    <template
      v-if="showHeader"
      #header
    >
      <h2 class="section-title heading-color">
        <span
          v-if="hasRecentSnow && canShowRecentSnowIcon"
          class="text-saturated-red-color"
        >
          •
        </span>
        {{ headerText }}
      </h2>
      <EstimatedBadge
        v-if="showBadge"
        additional-class-names="tw-ml-2"
      >
        Experimental
      </EstimatedBadge>
      <p class="tw-w-full tw-mt-0.5 tw-text-xs lg:tw-text-sm text-regular-color">
        {{ subHeaderText }}
      </p>
    </template>
    <template #body>
      <WeatherStationHeader
        :in-location-page="!!location"
        :show-weather-station-dialog="showWeatherStationDialog"
        :weather-station="weatherStation"
      />
      <div :class="contentClass">
        <div
          v-if="hasCam"
          :class="camContainerClass"
        >
          <ImageControls
            class="md:tw--mt-2.5"
            :image="cam"
            image-type="cam"
            :location="location"
            @show-image="handleShowImage('image')"
            @show-video="handleShowImage('video')"
          />
          <img
            :class="camClass"
            :src="camImageUrl"
          >
        </div>
        <div :class="chartClass">
          <WeatherStationSnowChart
            :has-cam="hasCam"
            :range="range"
            :weather-station="weatherStation"
          />
        </div>
      </div>
      <SnowForecastTableFooter
        :field="field"
        :source-id="sourceId"
        @click="handleShowDialog"
      />
      <AboutWeatherStationsDialog
        :message="dialogMessage"
        :read-more-link="dialogReadMoreLink"
        :show-dialog="showDialog"
        @close="handleCloseDialog"
      />
    </template>
  </Card>
</template>

<script>
import { mapState } from 'pinia';
import { isExperimentalSource } from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

import LocationWeatherStationMixin from '@@/components/Location/Mixins/LocationWeatherStationMixin';
import ShowImagesMixin from '@@/components/Location/Mixins/ShowImagesMixin';

export default {
  name: 'WeatherStationCard',

  mixins: [
    LocationWeatherStationMixin,
    ShowImagesMixin,
  ],

  props: {
    canShowRecentSnowIcon: {
      type: Boolean,
      default: false,
    },
    cardClassNames: {
      type: String,
      default: null,
    },
    headerText: {
      type: String,
      default: 'Live Snow',
    },
    location: {
      type: Object,
      required: true,
    },
    range: {
      type: Object,
      default: null,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    showWeatherStationDialog: {
      type: Boolean,
      default: false,
    },
    subHeaderText: {
      type: String,
      default: 'The latest snowfall data from weather stations, usually updated hourly.',
    },
    weatherStation: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showDialog: false,
    };
  },

  computed: {
    ...mapState(useMetaStore, ['dataSourcesById', 'getAttributionDetailUrl']),
    ...mapState(useUserStore, { units: (state) => state.preferences.units }),

    camClass() {
      return [
        'tw-rounded-md tw-overflow-hidden',
        this.$style.cam,
      ];
    },

    camContainerClass() {
      return [
        'tw-relative',
        'tw-w-full md:tw-w-1/5',
        'tw-mb-4 md:tw-mb-0 md:tw-mr-4',
      ];
    },

    cam() {
      return this.weatherStation?.cam;
    },

    camImageUrl() {
      return this.cam?.image_url;
    },

    chartClass() {
      return [
        'tw-w-full',
        this.hasCam ? 'md:tw-w-4/5' : '',
      ];
    },

    contentClass() {
      return 'tw-flex tw-flex-col md:tw-flex-row tw-mt-4';
    },

    dialogMessage() {
      return this.weatherStation?.source_attribution;
    },

    dialogReadMoreLink() {
      return this.getAttributionDetailUrl(this.sourceId, this.field) || '#';
    },

    field() {
      return 'observation_hourly';
    },

    hasCam() {
      return !!this.cam;
    },

    showBadge() {
      const fields = [this.field];
      return isExperimentalSource(this.sourceId, this.dataSourcesById, fields);
    },

    sourceId() {
      return this.weatherStation?.source_id;
    },
  },

  methods: {
    handleCloseDialog() {
      this.showDialog = false;
    },

    handleShowDialog($event) {
      if (this.dialogMessage) {
        $event.preventDefault();
        this.showDialog = true;
      }
    },

    handleShowImage(type) {
      const { location } = this;
      const image = { image: this.cam, location };
      this.showImage(image, type, [image]);
    },
  },
};
</script>

<style module>
.cam {
  aspect-ratio: 4/3;
}
</style>
